class PurchaseTypePicker extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.productInfo = this.closest('product-info');
    this.form = document.getElementById(this.dataset.form);
    this.option = this.dataset.option || 'subscription';
    this.subscriptionQty = parseInt(this.dataset.subscriptionQty, 10) || 1;

    try {
      this.variantMap = JSON.parse(this.querySelector('[data-purchase-type-data]')?.textContent || '{}');
    } catch {
      this.variantMap = {};
    }

    this.cards = this.querySelectorAll('.purchase-type__card');
    this.benefits = this.querySelector('.purchase-type__benefits');
    this.badge = this.querySelector('[data-purchase-type-badge]');
    this.subPrice = this.querySelector('[data-purchase-type-sub-price]');
    this.subCompare = this.querySelector('[data-purchase-type-sub-compare]');
    this.onePrice = this.querySelector('[data-purchase-type-one-price]');

    this.planSelect = this.productInfo?.querySelector('[data-purchase-type-frequency]');
    this.planEmpty = this.productInfo?.querySelector('[data-purchase-type-empty]');
    this.qtyInput = this.productInfo?.querySelector('quantity-input input');

    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        this.userChoseOption = input.value;
        this.setOption(input.value);
      });
    });

    this.planSelect?.addEventListener('change', () => this.updatePrices());

    if (window.theme?.pubsub) {
      this.variantUnsubscriber = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.variantChange,
        (event) => {
          if (event.data.productId?.toString() !== this.dataset.productId) return;
          this.handleVariant(event.data.variant?.id);
        }
      );
      this.quantityUnsubscriber = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.quantityBoundries,
        () => {
          if (this.option === 'subscription') {
            setTimeout(() => this.applyQuantity(), 0);
          }
        }
      );
    }

    this.productInfo?.addEventListener('variant:change', (event) => {
      this.handleVariant(event.detail?.variant?.id);
    });

    this.hideRechargeWidget();
    this.rememberFrequencyOptions();
    this.syncFrequencyOptions();
    this.setOption(this.option);
    this.updatePrices();
  }

  disconnectedCallback() {
    this.variantUnsubscriber?.();
    this.quantityUnsubscriber?.();
  }

  getVariantId() {
    const raw = this.productInfo?.querySelector('[data-selected-variant]')?.textContent;
    if (raw) {
      try {
        const id = JSON.parse(raw).id;
        if (id) return id.toString();
      } catch {
        /* Single-variant products omit the variant picker script. */
      }
    }

    const formVariantId = this.form?.querySelector('[name="id"]')?.value
      || this.productInfo?.querySelector('[name="id"]')?.value;
    if (formVariantId) return formVariantId.toString();

    const keys = Object.keys(this.variantMap || {});
    if (keys.length === 1) return keys[0];
    return null;
  }

  currentVariantData() {
    return this.variantMap[this.getVariantId()] || {};
  }

  selectedPlanId() {
    return this.planSelect?.value || Object.keys(this.currentVariantData().plans || {})[0];
  }

  rememberFrequencyOptions() {
    if (!this.planSelect || this.frequencyOptions) return;
    this.frequencyOptions = [...this.planSelect.options].map((option) => ({
      value: option.value,
      label: option.textContent.trim(),
      popular: option.hasAttribute('data-popular'),
      frequency: option.dataset.frequency || option.value,
    }));
  }

  syncFrequencyOptions(variantId) {
    if (!this.planSelect) return;
    this.rememberFrequencyOptions();
    const variant = variantId
      ? this.variantMap[variantId.toString()] || {}
      : this.currentVariantData();
    const plans = variant.plans || {};
    const availableIds = Object.keys(plans);
    const previous = this.planSelect.value;
    const available = this.frequencyOptions.filter((option) => availableIds.includes(option.value));
    const chosen = new Map();

    available.forEach((option) => {
      const price = plans[option.value]?.price ?? Number.POSITIVE_INFINITY;
      const current = chosen.get(option.frequency);
      if (!current || price < current.price || (price === current.price && !current.option.popular)) {
        chosen.set(option.frequency, { option, price });
      }
    });

    const seen = new Set();
    const kept = [];
    available.forEach((option) => {
      if (seen.has(option.frequency)) return;
      seen.add(option.frequency);
      kept.push(chosen.get(option.frequency).option);
    });

    const popular = kept.find((option) => option.popular);

    this.planSelect.replaceChildren();
    kept.forEach((option) => {
      const element = document.createElement('option');
      element.value = option.value;
      element.textContent = option.label;
      if (option.popular) element.setAttribute('data-popular', '');
      this.planSelect.append(element);
    });

    const previousOption = this.frequencyOptions.find((option) => option.value === previous);
    const sameFrequency = previousOption && kept.find((option) => option.frequency === previousOption.frequency);
    const stillAvailable = kept.some((option) => option.value === previous);
    const next = stillAvailable ? previous : sameFrequency?.value || popular?.value || kept[0]?.value || '';
    if (next) this.planSelect.value = next;
  }

  setOption(option) {
    const variant = this.currentVariantData();
    const canSubscribe = variant.hidden !== true && Object.keys(variant.plans || {}).length > 0;
    if (option === 'subscription' && !canSubscribe && this.dataset.requiresSellingPlan !== 'true') {
      option = 'onetime';
    }

    this.option = option;
    this.dataset.option = option;
    if (this.productInfo) this.productInfo.dataset.purchaseOption = option;

    this.cards.forEach((card) => {
      const input = card.querySelector('input[type="radio"]');
      const selected = input?.value === option;
      card.classList.toggle('is-selected', selected);
      if (input) input.checked = selected;
    });

    if (this.benefits) this.benefits.hidden = option !== 'subscription';

    if (this.planSelect) this.planSelect.disabled = option !== 'subscription';
    if (this.planEmpty) this.planEmpty.disabled = option === 'subscription';

    this.applyQuantity();
    this.updatePrices();
  }

  applyQuantity() {
    if (!this.qtyInput) return;
    if (this.option === 'subscription') {
      this.qtyInput.value = this.subscriptionQty;
      this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const min = parseInt(this.qtyInput.min || this.qtyInput.getAttribute('data-min') || '1', 10);
    if (parseInt(this.qtyInput.value, 10) === this.subscriptionQty) {
      this.qtyInput.value = min;
      this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  handleVariant(variantId) {
    const data = this.variantMap[variantId?.toString()] || {};
    const canSubscribe = data.hidden !== true && Object.keys(data.plans || {}).length > 0;
    this.classList.toggle('is-onetime-only', !canSubscribe);

    const subscribeInput = this.querySelector('input[value="subscription"]');
    if (subscribeInput) subscribeInput.disabled = !canSubscribe;

    this.syncFrequencyOptions(variantId);

    if (!canSubscribe && this.option === 'subscription') {
      this.setOption('onetime');
      return;
    }

    if (canSubscribe && this.option !== 'subscription' && this.userChoseOption !== 'onetime') {
      this.setOption('subscription');
      return;
    }

    this.updatePrices();
    requestAnimationFrame(() => this.applyQuantity());
  }

  formatMoney(cents) {
    if (window.theme?.Currency?.formatMoney) {
      return theme.Currency.formatMoney(
        cents,
        theme.settings?.moneyFormat || window.themeVariables?.settings?.moneyFormat
      );
    }
    return `£${(cents / 100).toFixed(2)}`;
  }

  updatePrices() {
    const variant = this.currentVariantData();
    if (variant.price == null) return;

    if (this.onePrice) this.onePrice.textContent = this.formatMoney(variant.price);

    const plan = variant.plans?.[this.selectedPlanId()] || Object.values(variant.plans || {})[0];
    if (!plan) return;

    const qty = this.subscriptionQty;
    const total = plan.price * qty;
    const compare = (plan.compareAt || variant.price) * qty;

    if (this.subPrice) this.subPrice.textContent = this.formatMoney(total);

    if (this.subCompare) {
      if (compare > total) {
        this.subCompare.hidden = false;
        this.subCompare.textContent = this.formatMoney(compare);
      } else {
        this.subCompare.hidden = true;
      }
    }

    if (this.badge) {
      const percent = compare > total ? Math.round(((compare - total) * 100) / compare) : 0;
      this.badge.hidden = percent <= 0;
      if (percent > 0) {
        if (!this.badge.dataset.template) this.badge.dataset.template = this.badge.textContent.replace(/\d+/, '{{ percent }}');
        this.badge.textContent = this.badge.dataset.template.replace('{{ percent }}', String(percent));
      }
    }
  }

  hideRechargeWidget() {
    const scope = this.productInfo?.closest('[id^="shopify-section-"]') || this.productInfo;
    if (!scope) return;

    const disable = () => {
      const wraps = [
        ...scope.querySelectorAll('[data-recharge-subscription-wrap], [id*="recharge_subscriptions_subscription"], [id*="recharge-subscriptions"]'),
      ];

      wraps.forEach((wrap) => {
        wrap.classList.add('recharge-subscription-hidden');
        wrap.querySelectorAll('input[name="selling_plan"], select[name="selling_plan"]').forEach((el) => {
          if (el.hasAttribute('data-purchase-type-frequency') || el.hasAttribute('data-purchase-type-empty')) return;
          el.disabled = true;
          el.removeAttribute('name');
        });
      });
    };

    disable();
    new MutationObserver(disable).observe(scope, { childList: true, subtree: true });
  }
}

if (!customElements.get('purchase-type-picker')) {
  customElements.define('purchase-type-picker', PurchaseTypePicker);
}

window.MypuraSubscriptionWidget = {
  init(productInfoId, hiddenMapId) {
    const productInfo = document.getElementById(productInfoId);
    const hiddenMapEl = document.getElementById(hiddenMapId);
    if (!productInfo || productInfo.dataset.subscriptionWidgetInit) return;

    productInfo.dataset.subscriptionWidgetInit = 'true';

    if (productInfo.querySelector('purchase-type-picker')) return;

    if (!hiddenMapEl) return;

    let hiddenMap = {};
    try {
      hiddenMap = JSON.parse(hiddenMapEl.textContent);
    } catch {
      return;
    }

    const scope = productInfo.closest('[id^="shopify-section-"]') || productInfo;
    const HIDDEN = 'recharge-subscription-hidden';
    const RC = '.rc-widget__root';
    const RC_SELECTORS = [
      '[id*="recharge_subscriptions_subscription"]',
      '[id*="recharge-subscriptions"]',
      '.recharge-subscription-widget',
      'recharge-subscription-widget',
    ];
    let knownWraps = [];
    let lastHandledVariantId = null;
    let pendingPurchaseForVariant = null;
    let retries = 0;
    let pendingUpdate;
    let pendingObserver;

    const queryShadow = (root, selector) => {
      const results = [];
      const walk = (node) => {
        if (!node?.querySelectorAll) return;
        node.querySelectorAll(selector).forEach((el) => results.push(el));
        node.querySelectorAll('*').forEach((el) => {
          if (el.shadowRoot) walk(el.shadowRoot);
        });
      };
      walk(root);
      if (root.shadowRoot) walk(root.shadowRoot);
      return results;
    };

    const discoverWraps = () => {
      const wraps = [];

      scope.querySelectorAll('[data-recharge-subscription-wrap]').forEach((wrap) => wraps.push(wrap));

      scope.querySelectorAll('.product-app-block').forEach((wrap) => {
        if (queryShadow(wrap, RC).length) wraps.push(wrap);
      });

      for (const selector of RC_SELECTORS) {
        const match = scope.querySelector(selector);
        if (!match) continue;
        const wrap = match.closest('[data-recharge-subscription-wrap], .product-app-block, .shopify-app-block, .shopify-block, [id^="shopify-block-"]') || match;
        if (!wraps.includes(wrap)) wraps.push(wrap);
      }

      const host = queryShadow(scope, RC)[0]?.getRootNode()?.host;
      const wrap = host?.closest('[data-recharge-subscription-wrap], .product-app-block, .shopify-app-block, .shopify-block, [id^="shopify-block-"]') || host;
      if (wrap && !wraps.includes(wrap)) wraps.push(wrap);

      return wraps;
    };

    const getWraps = () => {
      discoverWraps().forEach((wrap) => {
        if (!knownWraps.includes(wrap)) knownWraps.push(wrap);
      });
      knownWraps = knownWraps.filter((wrap) => document.contains(wrap));
      return knownWraps;
    };

    const isHidden = (variantId) => {
      const value = hiddenMap[variantId?.toString()];
      return value === true || value === 'true' || value === 1 || value === '1';
    };

    const getVariantId = () => {
      const raw = productInfo.querySelector('[data-selected-variant]')?.textContent;
      if (!raw) return null;
      try {
        return JSON.parse(raw).id;
      } catch {
        return null;
      }
    };

    const widgetReady = () => queryShadow(scope, RC).length > 0;

    const setVisibility = (hide) => {
      const wraps = getWraps();
      if (!wraps.length) return false;

      if (hide && !widgetReady()) return true;

      wraps.forEach((wrap) => wrap.classList.toggle(HIDDEN, hide));
      return true;
    };

    const selectOption = (value, attempt = 0) => {
      const input = queryShadow(scope, `input[name="purchaseOption"][value="${value}"]`)[0];
      if (!input) {
        if (attempt < 10) setTimeout(() => selectOption(value, attempt + 1), 200);
        return;
      }
      if (!input.checked) input.click();
    };

    const applyPurchaseOption = (id, hide) => {
      if (hide) {
        selectOption('onetime');
      } else {
        setTimeout(() => selectOption('subscription'), 100);
      }
      lastHandledVariantId = id;
      pendingPurchaseForVariant = null;
    };

    const update = (variantId) => {
      const id = variantId ?? getVariantId();
      if (!id) return;

      const hide = isHidden(id);
      const variantChanged = id !== lastHandledVariantId;
      const hasWrap = setVisibility(hide);

      if (!hasWrap) {
        if (variantChanged) pendingPurchaseForVariant = id;
        if (retries++ < 40) setTimeout(() => update(id), 400);
        return;
      }

      retries = 0;

      if (variantChanged || pendingPurchaseForVariant === id) {
        applyPurchaseOption(id, hide);
      }
    };

    const schedule = (variantId) => {
      clearTimeout(pendingUpdate);
      pendingUpdate = setTimeout(() => update(variantId), 50);
    };

    productInfo.querySelector('variant-picker')?.addEventListener('change', () => schedule());
    productInfo.addEventListener('variant:change', (e) => schedule(e.detail?.variant?.id));

    if (window.theme?.pubsub) {
      window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data.productId?.toString() !== productInfo.dataset.productId) return;
        schedule(event.data.variant?.id);
      });
    }

    new MutationObserver(() => {
      clearTimeout(pendingObserver);
      pendingObserver = setTimeout(() => schedule(), 200);
    }).observe(scope, { childList: true, subtree: true });

    window.addEventListener('load', () => schedule());
    update();
  },

  boot() {
    document.querySelectorAll('purchase-type-picker').forEach((el) => {
      if (el.dataset.bound !== 'true') {
        el.connectedCallback?.();
      }
    });

    document.querySelectorAll('[data-subscription-widget-init]').forEach((el) => {
      const productInfoId = el.dataset.productInfoId;
      if (productInfoId) this.init(productInfoId, el.id);
    });
  },
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => window.MypuraSubscriptionWidget.boot());
} else {
  window.MypuraSubscriptionWidget.boot();
}
